import { IBasePicker, IBasePickerSuggestionsProps, IInputProps, ITag, TagPicker, ValidationState } from '@fluentui/react/lib/Pickers';
import React, { useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { platformActions } from '../../../communication/Communication';
import * as Actions from '../../../store/actions';

// Minimum and maximum length for a tag
const minTagLength = 2;
const maxTagLength = 100;

export interface TagsEditorProps {
  tags: ITag[];
  allowCreatingNewTags?: boolean;
  tagListChanged: (items?: ITag[]) => void;
  validationStateChanged?: (isValid: boolean, message: string) => void;  // Callback to pass any new error messages to parent
}

// Tags editor component which relies on parent to maintain the tags state so that it can be reused acros different forms
export default function TagsEditorBasic(props: TagsEditorProps): JSX.Element {
  const [message, setMessage] = useState<string>(null);
  const picker = useRef<IBasePicker<ITag>>(null);

  const listContainsTagList = (tag: ITag, tagList?: ITag[]): boolean => {
    if (!tagList || !tagList.length || tagList.length === 0) {
      return false;
    }
    return tagList.some(compareTag => compareTag.key === tag.key);
  };

  const filterSelectedTags = (filterText: string, selectedItems: ITag[]): Promise<ITag[]> => {
    const url = `${Actions.Apis.TagsGet}?searchTerm=${filterText}&listSize=40`;

    return new Promise<ITag[]>((resolve, reject) => platformActions.get(false, url)
      .then(x => {
        resolve(x?.data?.map(t => ({
          key: t,
          name: t
        })));
      })
      .catch(err => reject(err)));
  };

  const getTextFromItem = (item: ITag): string => item.name;

  const getTagError = (tagValue: string): string => {
    const text = tagValue?.trim();
    if (!text || text?.length < minTagLength) {
      return `Mimimum ${minTagLength} characters are needed for new tag`;
    }
    return null;
  }


  const validateNewTag = (value: string): ValidationState => {
    if (!props.allowCreatingNewTags) {
      return ValidationState.invalid;
    }
    const error = getTagError(value);
    setMessage(value ? error : null);
    props.validationStateChanged?.(!error || !value, value ? error : null);
    return error ? ValidationState.invalid : ValidationState.valid;
  }

  const onItemSelected = React.useCallback((item: ITag): ITag | null => {
    if (picker.current && listContainsTagList(item, picker.current.items)) {
      return null;
    }
    return item;
  }, []);


  const pickerSuggestionsProps: IBasePickerSuggestionsProps = {
    suggestionsHeaderText: 'Suggested tags',
    noResultsFoundText: 'No matching tags found',
    loadingText: "Searching..."
  };

  const inputProps: IInputProps = {
    maxLength: maxTagLength,
    'aria-label': "Pick a tag",
    placeholder: "Pick a tag"
  };

  return <Wrapper className="tag-editor">
    <TagPicker
      selectedItems={props.tags}
      removeButtonAriaLabel="Remove"
      componentRef={picker}
      onResolveSuggestions={filterSelectedTags}
      resolveDelay={800}
      onItemSelected={onItemSelected}
      getTextFromItem={getTextFromItem}
      pickerSuggestionsProps={pickerSuggestionsProps}
      itemLimit={20}
      inputProps={inputProps}
      onChange={props.tagListChanged}
      onValidateInput={(input: string): ValidationState => validateNewTag(input)}
      createGenericItem={(input: string) => {
        return { key: input, name: input } as ITag
      }}
    />

    {message && <div className="error">{message}</div>}
  </Wrapper>
}

const Wrapper = styled.div`
  input {
    border: none;
    outline: dashed 1px #bbbbbb;
    min-width: 6rem;
    min-height: 2.4rem;
    margin: .2rem;
  }

  input:focus {
    outline: dashed 2px #bbbbbb;
  }

  > * {
    margin-top: var(--vertical-gap);
    margin-left: var(--vertical-gap);
  }

  .ms-BasePicker-text {
    padding: .6rem;
    border-color: var(--color-on-surface-lighter);
  }

  .ms-TagItem {
    border: solid 1px var(--color-surface-dark1);
    border-radius: .2rem;
  }

  /* @TODO: Find a better way via props to make specific tags readonly */
  .ms-TagItem-text[title="Azure DevOps" i] + .ms-TagItem-close,
  .ms-TagItem-text[title="CppCompiler" i] + .ms-TagItem-close,
  .ms-TagItem-text[title=".net" i] + .ms-TagItem-close,
  .ms-TagItem-text[title="visual studio" i] + .ms-TagItem-close,
  .ms-TagItem-text[title="Azure DevOps Server" i] + .ms-TagItem-close,
  .ms-TagItem-text[title="visual studio for mac" i] + .ms-TagItem-close {
    display: none;
  }
`;
